import { Injectable } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';
import { MatDateFormats } from '@angular/material/core';
import { DateTime } from 'ts-luxon';

const DATE_FORMAT_KEY = 'dateFormat';
const LOCALE_KEY = 'locale';
const TIMEZONE_KEY = 'timeZone';
/**
 * Returns the number of days between date range.
 */
export function getIntervalBetweenDates(startDate: string, endDate: string): number {
	const startDateWithDayEndTime = new Date(startDate).setHours(23, 59, 59, 999);
	const endDateWithDayEndTime = new Date(endDate).setHours(23, 59, 59, 999);

	// Calculate the difference in milliseconds
	const differenceInMilliseconds = endDateWithDayEndTime - startDateWithDayEndTime;

	// Convert milliseconds to days
	return Math.ceil(differenceInMilliseconds / (1000 * 60 * 60 * 24));
}

/**
 * Retrieves the stored date format from localStorage, or returns a default format
 * if none is found.
 *
 * @param defaultFormat - The default date format to return if no stored format is found (optional).
 * @returns - The stored date format or the default format.
 */
export function getStoredDateFormat(defaultFormat = 'mm/dd/yyyy'): string {
	return localStorage.getItem(DATE_FORMAT_KEY) || defaultFormat;
}

/**
 * Updates the provided date format string by replacing lowercase 'mm' (minutes)
 * with uppercase 'MM' (months) to standardize the format.
 *
 * @param format - The date format string to update.
 * @returns - The updated date format string.
 */
export function updateDateFormat(format: string): string {
	return format.replace(/mm/g, 'MM');
}

export function formatLuxonDateShort(date: DateTime): string {
	const locale = localStorage.getItem(LOCALE_KEY) ?? navigator.language ?? 'en-US';
	const dateWithLocale = date.setLocale(locale);

	const storedDateFormat = localStorage.getItem(DATE_FORMAT_KEY);
	const dateFormat = storedDateFormat ? updateDateFormat(storedDateFormat) : undefined;
	return dateFormat ? dateWithLocale.toFormat(dateFormat) : dateWithLocale.toLocaleString(DateTime.DATE_SHORT);
}

/**
 * CustomDateAdapter extends NativeDateAdapter to provide date formatting functionality
 * using the Luxon library. This adapter enables custom date format settings
 * based on user preferences or stored configurations for mat-datepicker.
 */
@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
	constructor() {
		super();
	}

	/**
	 * Formats a given date to a custom string format using Luxon.
	 * Retrieves the stored date format, corrects it if necessary, and
	 * applies it to the provided date using Luxon's formatting capabilities.
	 *
	 * @param date - The date to be formatted; can be a JavaScript Date object,
	 *               a string, or a Luxon DateTime instance.
	 * @returns - A string representing the formatted date according to
	 *            the specified or corrected date format.
	 */
	public override format(date: Date | string | DateTime): string {
		const dateFormat = getStoredDateFormat();
		const updatedDateFormat = updateDateFormat(dateFormat);

		// Attempt to parse the date based on its type
		const luxonDate = DateTime.isDateTime(date)
			? date
			: DateTime.fromISO(date instanceof Date ? date.toISOString() : date);

		return luxonDate.toFormat(updatedDateFormat);
	}
}

/**
 * Returns the number of days between date range.
 */
export function getIntervalBetweenDatesInSeconds(startDate: Date, endDate: Date): number {
	// Get the timestamps in milliseconds
	const timestamp1 = startDate.getTime();
	const timestamp2 = endDate.getTime();

	// Calculate the difference in milliseconds
	const differenceInMilliseconds = timestamp2 - timestamp1;

	// Convert milliseconds to seconds
	return Math.ceil(differenceInMilliseconds / 1000);
}

export function getEpochTime(date: string): number {
	// Parse the date string into a Date object
	const dateObj = new Date(date);

	// Get the epoch time in milliseconds
	const epochTime = Math.floor(dateObj.getTime());
	return epochTime;
}

/**
 * Formats a given date into a human-readable string.
 * @param dateToFormat - The date to format, can be a Date object or a string.
 * @returns A formatted string representation of the date.
 */
export function formatDateToHumanReadableString(dateToFormat: Date | string): string {
	const today = new Date();
	const dateObj = new Date(dateToFormat);

	if (!isNaN(dateObj.getTime())) {
		if (isSameDay(today, dateObj)) {
			return `Today, ${dateObj.toLocaleString([], {
				hour: 'numeric',
				minute: 'numeric',
				hour12: true
			})}`;
		} else if (isWithinLastWeek(today, dateObj)) {
			return dateObj.toLocaleString('en-US', {
				weekday: 'long',
				hour: 'numeric',
				minute: 'numeric',
				hour12: true
			});
		}
	}

	return dateObj.toLocaleDateString('en-US', {
		weekday: 'long',
		hour: 'numeric',
		minute: 'numeric',
		hour12: true
	});
}

// TODO: SN 7/Nov/24 Remove this function in CLP-1106 and consume locale format based on user preference
export function formatIsoString(isoDateString: string): string {
	// Create a Date object from the ISO string
	const date = new Date(isoDateString);

	// Format the date
	const formattedDate = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

	return formattedDate;
}
/**
 * Checks if two dates are on the same day.
 * @param date1 - The first date to compare.
 * @param date2 - The second date to compare.
 * @returns True if the dates are on the same day, false otherwise.
 */
export function isSameDay(date1: Date, date2: Date): boolean {
	return (
		date1.getFullYear() === date2.getFullYear() &&
		date1.getMonth() === date2.getMonth() &&
		date1.getDate() === date2.getDate()
	);
}

/**
 * Checks if a given date is within the last week from today.
 * @param today - The current date.
 * @param date - The date to check.
 * @returns True if the date is within the last week, false otherwise.
 */
export function isWithinLastWeek(today: Date, date: Date): boolean {
	const daysInMs = 24 * 60 * 60 * 1000;
	const diffInMs = today.getTime() - date.getTime();
	const diffInDays = Math.abs(Math.floor(diffInMs / daysInMs));
	return diffInDays <= 7;
}

/**
 * Returns a configuration object for custom date formats used by the MatDatePicker.
 * The formats include various representations of date inputs, as well as accessibility labels
 * for month and year selection.
 *
 * @returns - A MatDateFormats object containing the custom date formats and labels.
 */
export function getMatDateFormats(): MatDateFormats {
	return {
		parse: {
			dateInput: 'LL'
		},
		display: {
			dateInput: {
				yyyyMMDD: 'YYYY-MM-DD',
				ddMMYYYY: 'DD/MM/YYYY',
				mmDDYYYY: 'MM-DD-YYYY',
				ddMMYYYYDash: 'DD-MM-YYYY',
				ddMMYYYYDot: 'DD.MM.YYYY',
				mmDDYYYYSlash: 'MM/DD/YYYY',
				yyyyDDMM: 'YYYY.DD.MM',
				yyyyMMDDAlt: 'YYYY.MM.DD'
			},
			monthYearLabel: 'YYYY',
			dateA11yLabel: 'LL',
			monthYearA11yLabel: 'YYYY'
		}
	};
}

/**
 * Stores the time zone in localStorage.
 * @param timeZone - The time zone string to store (e.g., 'America/Panama').
 */
export function setTimeZone(timeZone: string): void {
	localStorage.setItem(TIMEZONE_KEY, timeZone);
}

/**
 * Retrieves the stored time zone from localStorage.
 * @returns The stored time zone as a string or null if not found.
 */
export function getTimeZone(defaultTimeZone = 'local'): string {
	return localStorage.getItem(TIMEZONE_KEY) || defaultTimeZone;
}

/**
 *
 * @param utcTime Converts UTC time to user specific time utilizing the time zone and date format
 * selected in the my-account page.
 * @returns a string
 */
export function formatToUserSpecificTime(utcTime: string): string {
	const timeZone = 'local';
	const dateFormat = getStoredDateFormat();
	const userTime = DateTime.fromISO(utcTime, { zone: 'utc' })
		.setZone(timeZone)
		.toFormat(updateDateFormat(dateFormat) + ' hh:mm a');
	return userTime;
}

/**
 * Returns the current date in UTC format as a string.
 */
export function getCurrentDateInUTC(): string {
	const currentDate = new Date();
	const currentdateFormat = new Date(currentDate.getTime() + 10000);
	return currentdateFormat.toISOString().slice(0, 19) + 'Z';
}
/**
 * Comparator function to sort rows by the 'sentAt' date
 * @param date1
 * @param date2
 *  returns a number indicating their chronological order. Handles invalid or missing date inputs.
 */
export function dateComparator(date1: string | null | undefined, date2: string | null | undefined): number {
	const parseDate = (dateString: string | null | undefined): number | null => {
		if (!dateString) return null;
		const timestamp = new Date(dateString).getTime();
		return isNaN(timestamp) ? null : timestamp;
	};

	const date1Number = parseDate(date1);
	const date2Number = parseDate(date2);

	if (date1Number === null && date2Number === null) {
		return 0;
	}

	if (date1Number === null) {
		return -1;
	} else if (date2Number === null) {
		return 1;
	}

	return date1Number - date2Number;
}

/**
 * Converts a timestamp to a formatted date and time string based on the user's selected timezone and date format.
 * @param timeZone - A string representation of the user selected timeZone.
 * @param dateFormat - A string representation of the user selected dateFormat.
 * @param timeStamp - A string representation of the date and time in ISO format.
 * @returns A formatted string representing the converted date and time, or the original timestamp if conversion fails.
 */
export function getConvertedDateTime(
	timeZone: string | undefined,
	dateFormat: string | undefined,
	timeStamp: string
): string {
	const zone = timeZone ?? 'UTC';
	const inputDateFormat = dateFormat ?? 'yyyy-MM-dd';
	return DateTime.fromISO(timeStamp).setZone(zone).toFormat(`${inputDateFormat} h:mm a`);
}

/**
 * Formats a UTC timestamp into a date string like 'MM/dd/yyyy',
 * based on user's timezone and preferred date format.
 *
 * @param timeZone - Timezone string (e.g., 'Asia/Kolkata', 'America/New_York')
 * @param dateFormat - Format string compatible with Luxon (e.g., 'dd/MM/yyyy')
 * @param timeStamp - ISO timestamp to convert (e.g., '2025-07-22T10:15:00Z')
 * @returns A formatted date string
 */
export function getConvertedDate(
	timeZone: string | undefined,
	dateFormat: string | undefined,
	timeStamp: string
): string {
	const zone = timeZone ?? 'UTC';
	const format = dateFormat ?? 'yyyy-MM-dd';
	return DateTime.fromISO(timeStamp).setZone(zone).toFormat(format);
}

/**
 * Calculates days left until invite expiry, capped at 7 days.
 * Takes into account time zone and normalizes to start of day.
 *
 * @param expiresAtISO - ISO string of the `inviteExpiresAt` date
 * @param timeZone - Optional IANA time zone string (e.g., 'America/Chicago'); defaults to 'UTC'
 * @returns number of days left (0 if expired, max 7)
 */
export function getInviteExpiryDays(expiresAtISO: string, timeZone = 'UTC'): number {
	const now = DateTime.now().setZone(timeZone).startOf('day');
	const expireDate = DateTime.fromISO(expiresAtISO).setZone(timeZone).startOf('day');

	const daysLeft = Math.ceil(expireDate.diff(now, 'days').days);
	return Math.max(0, Math.min(daysLeft, 7));
}

/**
 * Converts a given UTC date and time string to the user's local timezone.
 * Formats the result as 'yyyy-MM-dd hh:mm a' (e.g., '2025-05-12 11:56 PM').
 *
 * @param date - The date string in ISO format (e.g., '2025-05-12')
 * @param time - The time string in 24-hour format (e.g., '23:56')
 * @returns A formatted date-time string in the user's local timezone.
 */
export function convertToUserTimeZone(date: string, time: string): string {
	const userTime = DateTime.fromISO(`${date}T${time}`, { zone: 'utc' })
		.setZone('local')
		.toFormat('yyyy-MM-dd hh:mm a');
	return userTime;
}
